import { merge, type Subscription } from 'rxjs';

import { CdkColumnDef } from '@angular/cdk/table';
import { ENTER, SPACE } from '@angular/cdk/keycodes';
import {
  Input,
  inject,
  Component,
  type OnInit,
  type OnDestroy,
  booleanAttribute,
  ChangeDetectorRef,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  TableSort,
  type TableSortable,
  type TableSortDirection,
  APP_TABLE_SORT_DEFAULT_OPTIONS,
  type TableSortHeaderArrowPosition,
} from './sort';

@Component({
  selector: '[appTableSortHeader]',
  exportAs: 'appTableSortHeader',
  templateUrl: './sort-header.html',
  styleUrl: './sort-header.scss',
  host: {
    class: 'app-table-sort-header',
    '(click)': '_handleClick()',
    '(keydown)': '_handleKeydown($event)',
    '[class.app-table-sort-header-disabled]': '_isDisabled()',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableSortHeader implements TableSortable, OnInit, OnDestroy {
  protected readonly _sort = inject(TableSort, { optional: true })!;
  private readonly _columnDef = inject(CdkColumnDef, { optional: true });
  private readonly _changeDetectorRef = inject(ChangeDetectorRef);

  @Input('appTableSortHeader')
  id!: string;

  @Input()
  start!: TableSortDirection;

  @Input()
  arrowPosition: TableSortHeaderArrowPosition = 'after';

  @Input({ transform: booleanAttribute })
  disabled = false;

  @Input({ transform: booleanAttribute })
  disableClear!: boolean;

  _arrowDirection: TableSortDirection = '';

  private _renderChanges?: Subscription;

  constructor() {
    const defaultOptions = inject(APP_TABLE_SORT_DEFAULT_OPTIONS, {
      optional: true,
    });

    if (defaultOptions?.arrowPosition) {
      this.arrowPosition = defaultOptions?.arrowPosition;
    }
  }

  ngOnInit(): void {
    if (!this.id && this._columnDef) {
      this.id = this._columnDef.name;
    }

    this._sort.register(this);
    this._renderChanges = merge(this._sort.stateChanges$, this._sort.sortChange).subscribe(() => {
      if (this._isSorted()) {
        this._updateArrowDirection();
      }

      this._changeDetectorRef.markForCheck();
    });
    this._updateArrowDirection();
  }

  ngOnDestroy(): void {
    this._sort.deregister(this);
    this._renderChanges?.unsubscribe();
  }

  get isDescSorting(): boolean {
    return this._isSorted() && this._arrowDirection === 'desc';
  }

  _toggleOnInteraction(): void {
    this._sort.sort(this);
  }

  _handleClick(): void {
    if (!this._isDisabled()) {
      this._toggleOnInteraction();
    }
  }

  _handleKeydown(event: KeyboardEvent): void {
    if (!this._isDisabled() && (event.keyCode === SPACE || event.keyCode === ENTER)) {
      event.preventDefault();
      this._toggleOnInteraction();
    }
  }

  _isSorted(): boolean {
    return this._sort.active == this.id && (this._sort.direction === 'asc' || this._sort.direction === 'desc');
  }

  _updateArrowDirection(): void {
    this._arrowDirection = this._isSorted() ? this._sort.direction : this.start || this._sort.start;
  }

  _isDisabled(): boolean {
    return this._sort.disabled || this.disabled;
  }

  _renderArrow(): boolean {
    return !this._isDisabled() || this._isSorted();
  }
}
